import {
  useEpgSettings,
  type EpgFontSize,
  type EpgLayoutMode,
  type EpgOpacity,
  type EpgPreviewSize,
  type EpgTimeWindow,
} from "../epg/settingsStore.js";
import { Link } from "react-router";

/**
 * Guide settings — everything that used to sit in the guide's own toolbar.
 *
 * The guide is the one screen driven from a remote on a television, where each
 * extra control is another press to get past on the way to a channel. Layout,
 * transparency, text size and the time window are set once and then left alone,
 * so they belong here; only the category picker and the channel box, which are
 * how you actually navigate, stay in the guide. Sparkle arranges its own guide
 * options the same way, under Settings rather than over the grid.
 */

/** One row of mutually exclusive choices, rendered big enough to hit with a d-pad. */
function ChoiceRow<T extends string | number>({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="guide-setting">
      <div className="guide-setting__text">
        <span className="guide-setting__label">{label}</span>
        {hint && <span className="guide-setting__hint">{hint}</span>}
      </div>
      <div className="guide-setting__choices" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={String(option.value)}
            type="button"
            className={`guide-choice${option.value === value ? " guide-choice--active" : ""}`}
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function GuideSettingsPage() {
  const layout = useEpgSettings((s) => s.layout);
  const setLayout = useEpgSettings((s) => s.setLayout);
  const timeWindow = useEpgSettings((s) => s.timeWindow);
  const setTimeWindow = useEpgSettings((s) => s.setTimeWindow);
  const opacity = useEpgSettings((s) => s.opacity);
  const setOpacity = useEpgSettings((s) => s.setOpacity);
  const fontSize = useEpgSettings((s) => s.fontSize);
  const setFontSize = useEpgSettings((s) => s.setFontSize);
  const previewSize = useEpgSettings((s) => s.previewSize);
  const setPreviewSize = useEpgSettings((s) => s.setPreviewSize);

  return (
    <div className="page page--guide-settings">
      <div className="settings-card">
        <div className="settings-header">
          <h1 className="settings-title">Guide</h1>
          <p className="settings-subtitle">
            How the TV guide looks and how much of the schedule it shows. The guide itself keeps only the
            category picker and the channel box, so there is less to step past with a remote.
          </p>
        </div>

        <ChoiceRow<EpgLayoutMode>
          label="Layout"
          hint="Traditional puts a preview above the grid. Overlay draws the grid over the playing channel."
          value={layout}
          options={[
            { value: "traditional", label: "Traditional" },
            { value: "overlay", label: "Overlay" },
          ]}
          onChange={setLayout}
        />

        {layout === "overlay" && (
          <ChoiceRow<EpgOpacity>
            label="Transparency"
            hint="How much of the playing channel shows through the grid."
            value={opacity}
            options={[
              { value: "light", label: "Light" },
              { value: "medium", label: "Medium" },
              { value: "heavy", label: "Heavy" },
            ]}
            onChange={setOpacity}
          />
        )}

        {layout === "traditional" && (
          <ChoiceRow<EpgPreviewSize>
            label="Preview size"
            hint="How much width the preview takes; the rest goes to the programme details."
            value={previewSize}
            options={[
              { value: "s", label: "Small" },
              { value: "m", label: "Medium" },
              { value: "l", label: "Large" },
            ]}
            onChange={setPreviewSize}
          />
        )}

        <ChoiceRow<EpgFontSize>
          label="Text size"
          hint="Larger text is easier to read from across a room."
          value={fontSize}
          options={[
            { value: "s", label: "Small" },
            { value: "m", label: "Medium" },
            { value: "l", label: "Large" },
          ]}
          onChange={setFontSize}
        />

        <ChoiceRow<EpgTimeWindow>
          label="Hours shown"
          hint="How many hours fit across the guide. Scroll right for up to 36 hours."
          value={timeWindow}
          options={[
            { value: 2, label: "2 hours" },
            { value: 4, label: "4 hours" },
          ]}
          onChange={setTimeWindow}
        />
      </div>

      <p className="guide-settings__pointer">
        Refreshing the guide and the channel-status report live in{" "}
        <Link to="/settings">Settings → Sync &amp; Health</Link>, with the rest of the
        "is my provider healthy" controls.
      </p>
    </div>
  );
}
